import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { followUpBucket, leadNextDate, statusClass, toDateTime } from "../lib/helpers";
import { log } from "../lib/logger";
import { ListChecks, MapPin, TrendingUp, Trophy, AlertTriangle, Car, Building2, Loader2 } from "lucide-react";

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="sohom-card p-5 flex items-start justify-between" data-testid={testid}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-3xl font-bold mt-2 text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{value}</div>
      </div>
      <div className="rounded-lg p-2.5" style={{ background: `${color}1a`, color }}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

function TravelCard({ travel, role, officeToday, officeBusy, onPunchOffice }) {
  const isFE = role === "field_executive";
  const showBreakdown = !isFE && (travel.today_by_user?.length || 0) > 0;
  const punches = officeToday?.count ?? 0;
  return (
    <div className="sohom-card p-5 mb-7" data-testid="travel-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2.5 bg-sky-50 text-sky-700">
            <Car className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {isFE ? "Your travel" : "Team travel"}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              <span className="text-2xl font-bold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}} data-testid="travel-today-km">
                {travel.today_km}
              </span>
              <span className="text-slate-500"> km today</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="font-semibold text-slate-900">{travel.month_km}</span>
              <span className="text-slate-500"> km this month</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-slate-500">{travel.today_visits} visit{travel.today_visits === 1 ? "" : "s"} today</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onPunchOffice}
            disabled={officeBusy}
            className="sohom-btn-secondary"
            data-testid="punch-office-btn"
            title="Tap when you arrive at office — used as an intermediate waypoint in travel calc"
          >
            {officeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
            {officeBusy ? "Capturing…" : `At office${punches > 0 ? ` · ${punches}×` : ""}`}
          </button>
          <Link to="/reports" className="sohom-link text-sm whitespace-nowrap" data-testid="travel-see-report">See report →</Link>
        </div>
      </div>
      {showBreakdown && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Today by field executive</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {travel.today_by_user.map((u) => (
              <div key={u.user_id} className="flex items-baseline justify-between text-sm border border-slate-100 rounded-md px-3 py-2" data-testid={`travel-fe-${u.user_id}`}>
                <span className="font-medium text-slate-800 truncate mr-2">{u.user_name}</span>
                <span className="text-slate-500 whitespace-nowrap">
                  <strong className="text-sky-700">{u.distance_km}</strong> km · {u.visit_count} visit{u.visit_count === 1 ? "" : "s"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [travel, setTravel] = useState(null);
  const [officeToday, setOfficeToday] = useState(null);
  const [officeBusy, setOfficeBusy] = useState(false);
  const [travelReloadTick, setTravelReloadTick] = useState(0);

  useEffect(() => {
    api.get("/leads?light=1").then((r) => setLeads(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, o] = await Promise.all([
          api.get("/travel/me/today"),
          api.get("/travel/office-checkin/today"),
        ]);
        if (!cancelled) { setTravel(t.data); setOfficeToday(o.data); }
      } catch (loadErr) { log.warn("[Sohom] travel endpoints unavailable, hiding card:", loadErr); }
    })();
    return () => { cancelled = true; };
  }, [travelReloadTick]);

  const punchAtOffice = () => {
    if (!navigator.geolocation) { alert("Geolocation not available."); return; }
    setOfficeBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/travel/office-checkin", {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
            note: "",
          });
          setTravelReloadTick((t) => t + 1);
        } catch (ex) {
          alert(ex?.response?.data?.detail || "Couldn't check in at office. Please try again.");
        } finally { setOfficeBusy(false); }
      },
      (e) => { setOfficeBusy(false); alert("Location denied: " + e.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const stats = useMemo(() => {
    const s = { total: leads.length, NEW: 0, IN_PROGRESS: 0, WON: 0 };
    leads.forEach((l) => {
      const st = (l.status || "NEW").toUpperCase();
      if (st === "NEW") s.NEW++;
      else if (st === "IN_PROGRESS") s.IN_PROGRESS++;
      else if (st === "WON") s.WON++;
    });
    return s;
  }, [leads]);

  const upcoming = useMemo(() => {
    const items = leads
      .map((l) => ({ lead: l, date: leadNextDate(l) }))
      .filter((x) => x.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return items;
  }, [leads]);

  const reminderCounts = useMemo(() => {
    const c = { overdue: 0, today: 0, tomorrow: 0 };
    upcoming.forEach((x) => {
      const b = followUpBucket(x.date);
      if (b?.key === "overdue") c.overdue++;
      else if (b?.key === "today") c.today++;
      else if (b?.key === "tomorrow") c.tomorrow++;
    });
    return c;
  }, [upcoming]);

  const recent = useMemo(() => leads.slice(0, 6), [leads]);
  const showReminders = user?.role === "field_executive" && (reminderCounts.overdue + reminderCounts.today + reminderCounts.tomorrow) > 0;

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-7xl">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">A quick pulse on your lead pipeline.</p>
          </div>
          <Link to="/leads" className="sohom-link text-sm whitespace-nowrap" data-testid="view-all-leads">View all leads →</Link>
        </div>

        {showReminders && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex flex-wrap items-center gap-4" data-testid="follow-up-reminders">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-bold uppercase tracking-wider text-sm">Follow-up Reminders</span>
            </div>
            <div className="flex gap-3 text-sm">
              <span className="font-medium"><span className="text-red-700 font-bold">{reminderCounts.overdue}</span> Overdue</span>
              <span className="text-red-300">·</span>
              <span className="font-medium"><span className="text-amber-700 font-bold">{reminderCounts.today}</span> Due Today</span>
              <span className="text-red-300">·</span>
              <span className="font-medium"><span className="text-blue-700 font-bold">{reminderCounts.tomorrow}</span> Tomorrow</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
          <StatCard label="Total Leads" value={loading ? "…" : stats.total} icon={ListChecks} color="#0284C7" testid="stat-total" />
          <StatCard label="New" value={loading ? "…" : stats.NEW} icon={MapPin} color="#00AEEF" testid="stat-new" />
          <StatCard label="In Progress" value={loading ? "…" : stats.IN_PROGRESS} icon={TrendingUp} color="#F59E0B" testid="stat-inprogress" />
          <StatCard label="Won" value={loading ? "…" : stats.WON} icon={Trophy} color="#10B981" testid="stat-won" />
        </div>

        {travel && (
          <TravelCard travel={travel} role={user?.role} officeToday={officeToday} officeBusy={officeBusy} onPunchOffice={punchAtOffice} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 sohom-card p-5" data-testid="upcoming-followups">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Upcoming Follow-ups</h3>
              <Link to="/follow-ups" className="text-xs sohom-link" data-testid="view-all-followups">See all →</Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No upcoming follow-ups.</div>
            ) : (
              <ul className="space-y-3">
                {upcoming.slice(0, 8).map(({ lead, date }) => {
                  const b = followUpBucket(date);
                  return (
                    <li key={lead.id} className="flex items-start gap-3">
                      <span className={`sohom-chip ${b.cls} shrink-0`}>{b.label}</span>
                      <Link to={`/leads/${lead.id}`} className="text-sm hover:text-sky-700">
                        <div className="font-medium text-slate-800">{lead.building_name || "Unnamed"}</div>
                        <div className="text-xs text-slate-500">{lead.locality || lead.city} · {lead.lead_no}</div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="lg:col-span-2 sohom-card overflow-hidden" data-testid="recent-leads">
            <div className="px-5 pt-5 pb-3 flex justify-between items-baseline">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Recent Leads</h3>
              <Link to="/leads" className="text-xs sohom-link">See all</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="sohom-table">
                <thead>
                  <tr>
                    <th>Lead #</th>
                    <th>Building</th>
                    <th>City</th>
                    <th>Units</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-slate-400 py-8">No leads yet.</td></tr>
                  ) : recent.map((l) => (
                    <tr key={l.id} onClick={() => window.location.assign(`/leads/${l.id}`)}>
                      <td className="font-mono text-slate-700">{l.lead_no}</td>
                      <td className="font-medium text-slate-900">{l.building_name || "Unnamed"}</td>
                      <td>{l.city || "—"}</td>
                      <td>{l.estimated_units || 0}</td>
                      <td><span className={statusClass(l.status)}>{(l.status || "NEW").replace("_", " ")}</span></td>
                      <td className="text-slate-500">{toDateTime(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
